// Procedural hero pose library. Poses are data (two keyframes blended by weight), so a proposed animation can be
// previewed side by side with the live one in the animation lab (?animlab) before the owner approves it.
// The game uses ACTIVE_LANCE; the lab can show any entry of LANCE_POSES.
import type { HeroRig } from './models';

/** One pose keyframe, as joint rotations (radians) added on top of neutral. drop = how far the root sinks. */
export interface PoseKey {
  torso: [number, number]; body: number; head: [number, number];
  armR: [number, number, number]; armL: [number, number, number]; foreR: number; foreL: number;
  legR: [number, number]; legL: [number, number]; shinR: number; shinL: number; drop: number;
}
export interface LancePose {
  label: string; note: string;
  /** charge phase: hands gather energy */
  windup: PoseKey;
  /** release phase: beam is firing */
  thrust: PoseKey;
  /** blend speeds (1/s): into wind-up, out of wind-up, into thrust, out of thrust */
  rates: [number, number, number, number];
  /** recoil shake amplitude while firing */
  shake: number;
}

const K = (k: Partial<PoseKey>): PoseKey => ({
  torso: [0, 0], body: 0, head: [0, 0], armR: [0, 0, 0], armL: [0, 0, 0], foreR: 0, foreL: 0,
  legR: [0, 0], legL: [0, 0], shinR: 0, shinL: 0, drop: 0, ...k,
});

export const LANCE_POSES: Record<string, LancePose> = {
  current: {
    label: 'Cupped hip (live until 2026-11-01)',
    note: 'Cupped hands at the right hip with the chest coiled away, then a two-palm thrust with wrists together.',
    windup: K({ torso: [0.08, -0.95], body: 0.12, head: [-0.1, 0.85], armR: [0.1, 0, 0.3], armL: [-0.3, 0, -0.9], foreR: -0.8, foreL: 0,
      legL: [-0.35, 0.22], legR: [0.15, -0.22], shinL: 0.55, shinR: 0.4, drop: 0.1 }),
    thrust: K({ torso: [-0.06, 0], body: 0.2, head: [-0.15, 0], armR: [-1.5, 0, 0.46], armL: [-1.5, 0, -0.46], foreR: -0.04, foreL: -0.04,
      legL: [-0.55, 0.1], legR: [0.45, -0.1], shinL: 0.45, shinR: 0.15, drop: 0.08 }),
    rates: [9, 14, 22, 8], shake: 0.025,
  },
  previous: {
    label: 'Previous (before today)',
    note: 'Braced stance, arms held out in front with bent elbows while charging, then straightened.',
    windup: K({ armR: [-0.95, -0.25, 0.1], armL: [-0.95, 0.25, -0.1], foreR: -1.05, foreL: -1.05, legL: [-0.45, 0], legR: [0.35, 0], shinL: 0.35, shinR: 0.2 }),
    thrust: K({ armR: [-1.45, -0.25, 0.1], armL: [-1.45, 0.25, -0.1], foreR: -0.15, foreL: -0.15, legL: [-0.45, 0], legR: [0.35, 0], shinL: 0.35, shinR: 0.2 }),
    rates: [60, 60, 60, 60], shake: 0,
  },
  'deep-lunge': {
    label: 'Deep coil + lunge (live, owner-approved)',
    note: 'Same hand placement, but a deeper twist and lower crouch on the charge, and a long forward lunge with more lean on release.',
    windup: K({ torso: [0.1, -1.25], body: 0.18, head: [-0.12, 1.1], armR: [0.1, 0, 0.3], armL: [-0.3, 0, -0.9], foreR: -0.8, foreL: 0,
      legL: [-0.5, 0.3], legR: [0.25, -0.3], shinL: 0.8, shinR: 0.6, drop: 0.2 }),
    thrust: K({ torso: [-0.1, 0.1], body: 0.32, head: [-0.25, -0.1], armR: [-1.55, 0, 0.46], armL: [-1.55, 0, -0.46], foreR: -0.04, foreL: -0.04,
      legL: [-0.85, 0.12], legR: [0.7, -0.12], shinL: 0.6, shinR: 0.1, drop: 0.15 }),
    rates: [7, 14, 26, 8], shake: 0.03,
  },
};
export const ACTIVE_LANCE = 'deep-lunge';

/** Smoothed blend weights for a pose; call every frame. */
export class LanceDrive {
  windup = 0; thrust = 0;
  update(charging: boolean, releasing: boolean, dt: number, P: LancePose): { wu: number; th: number } {
    const [wi, wo, ti, to] = P.rates;
    this.windup += ((charging ? 1 : 0) - this.windup) * Math.min(1, dt * (charging ? wi : wo));
    this.thrust += ((releasing ? 1 : 0) - this.thrust) * Math.min(1, dt * (releasing ? ti : to));
    return { wu: this.windup * (1 - this.thrust), th: this.thrust };
  }
}

/** Writes the blended pose onto the rig's arms/legs/torso. Returns how far to lower the root. */
export function poseLance(rig: HeroRig, wu: number, th: number, t: number, P: LancePose): number {
  const a = P.windup, b = P.thrust, m = (x: number, y: number) => x * wu + y * th;
  const shake = th * Math.sin(t * 61) * P.shake;
  rig.torso.rotation.x = m(a.torso[0], b.torso[0]); rig.torso.rotation.y = m(a.torso[1], b.torso[1]) + shake;
  rig.body.rotation.x = m(a.body, b.body) + shake; rig.body.rotation.z = 0;
  rig.head.rotation.x = m(a.head[0], b.head[0]); rig.head.rotation.y = m(a.head[1], b.head[1]);
  rig.armR.rotation.set(m(a.armR[0], b.armR[0]), m(a.armR[1], b.armR[1]), m(a.armR[2], b.armR[2]));
  rig.armL.rotation.set(m(a.armL[0], b.armL[0]), m(a.armL[1], b.armL[1]), m(a.armL[2], b.armL[2]));
  rig.foreR.rotation.x = m(a.foreR, b.foreR); rig.foreL.rotation.x = m(a.foreL, b.foreL);
  rig.legR.rotation.set(m(a.legR[0], b.legR[0]), 0, m(a.legR[1], b.legR[1]));
  rig.legL.rotation.set(m(a.legL[0], b.legL[0]), 0, m(a.legL[1], b.legL[1]));
  rig.shinR.rotation.x = m(a.shinR, b.shinR); rig.shinL.rotation.x = m(a.shinL, b.shinL);
  return m(a.drop, b.drop);
}
